import { motion } from 'motion/react'
import { ArrowRight, Phone, Shield, Network, Headphones, MessageSquare, Calendar } from 'lucide-react'
import { Button, Badge } from '@/components/ui'
import { companyInfo } from '@/data/company'

const CALENDAR_LINK = 'https://meetings.hubspot.com/markus98/ucadvisors-quick-chat?embed=true&uuid=052b5298-7c6e-4b12-bde4-f8f26d01c9b3'

export function Hero() {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-hero-pattern" />
      <div className="absolute inset-0 bg-mesh opacity-30" />
      
      {/* Animated gradient orbs */}
      <motion.div
        className="absolute top-20 left-10 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-80 h-80 bg-electric-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.2, 0.4],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Badge variant="glow" className="mb-6">
                Cybersecurity-First. Provider-Agnostic. Data-Backed.
              </Badge>
            </motion.div>

            <h1 className="text-display-lg md:text-display-xl lg:text-display-2xl font-heading font-bold text-white mb-6">
              Optimize Your{' '}
              <span className="text-gradient">Entire Organization</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-xl">
              We holistically assess security, network, communications, and operations—then optimize your full technology stack starting with cybersecurity.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a href={CALENDAR_LINK} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="glow" className="group">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book a 15-min Call
                  <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a href={`tel:${companyInfo.phoneRaw}`}>
                <Button size="lg" variant="secondary">
                  <Phone className="w-5 h-5" />
                  {companyInfo.phone}
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-6 items-center">
              {companyInfo.stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-400">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Visual element - Security-first service grid */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              {/* Main card */}
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Shield, label: 'Cybersecurity', color: 'from-accent-500 to-accent-600', primary: true },
                    { icon: Network, label: 'Network', color: 'from-accent-400 to-electric-500' },
                    { icon: MessageSquare, label: 'UCaaS', color: 'from-electric-500 to-electric-600' },
                    { icon: Headphones, label: 'CCaaS', color: 'from-electric-400 to-accent-500' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                      className={`glass-card-hover p-6 text-center cursor-pointer ${item.primary ? 'ring-2 ring-accent-500/50' : ''}`}
                    >
                      <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-3 shadow-glow`}>
                        <item.icon className="w-7 h-7 text-white" />
                      </div>
                      <span className="text-sm font-medium text-white">{item.label}</span>
                      {item.primary && (
                        <div className="text-2xs text-accent-400 mt-1">Foundation</div>
                      )}
                    </motion.div>
                  ))}
                </div>
                
                {/* Flow indicator */}
                <div className="mt-6 text-center">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">
                    Security First → Network → Communications
                  </div>
                </div>
              </div>

              {/* Floating badges at bottom */}
              <div className="flex flex-wrap gap-4 mt-6 justify-center items-center">
                {/* NOC Monitored badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className="glass-card px-4 py-3 rounded-xl shadow-glow"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-success-500/20 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-success-500 animate-pulse" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400">UC Care Clients</div>
                      <div className="text-sm font-semibold text-white">NOC Monitored</div>
                      <div className="text-2xs text-slate-500">&lt;15 min response SLA</div>
                    </div>
                  </div>
                </motion.div>

                {/* Zoom certified badge - links to partner directory */}
                <motion.a
                  href="https://partner.zoom.us/partner-locator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                  className="glass-card px-4 py-3 rounded-xl hover:bg-white/10 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3">
                    <svg viewBox="0 0 32 32" className="w-10 h-10" fill="none">
                      <rect width="32" height="32" rx="6" fill="#2D8CFF"/>
                      <path d="M8 10.5C8 9.67 8.67 9 9.5 9H15.5C16.33 9 17 9.67 17 10.5V21.5C17 22.33 16.33 23 15.5 23H9.5C8.67 23 8 22.33 8 21.5V10.5Z" fill="white"/>
                      <path d="M18 13L24 9.5V22.5L18 19V13Z" fill="white"/>
                    </svg>
                    <div className="text-xs">
                      <div className="text-slate-400">Zoom</div>
                      <div className="text-white font-medium group-hover:text-accent-400 transition-colors">Certified Partner</div>
                      <div className="text-2xs text-slate-500">View in directory →</div>
                    </div>
                  </div>
                </motion.a>

                {/* Zoom Up Partner Program Badge */}
                <motion.a
                  href="https://partner.zoom.us/partner-locator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0, duration: 0.5 }}
                  className="hover:scale-105 transition-transform cursor-pointer glass-card rounded-xl p-3"
                >
                  <img 
                    src="/zoom-up-partner-badge.png" 
                    alt="Zoom Up Partner Program - Strategic Advisor - Services: Deployment Phone, Support Workplace & Phone" 
                    className="h-24 w-auto"
                  />
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
